import { message } from "antd";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { noAuthClient } from "services/axios";
import {
  createBulkComments,
  CommentAttachmentResponse
} from "services/comment-service";
import debounce from "lodash.debounce";
import { FeatureComments } from "entity-app/models/feature-comments";
import { GET_FEATURE_COMMENTS } from "entity-app/graphQL/ciq-feature-queries";
import { hasTheFeature } from "entity-app/utils/utils";
import { ErrorMessages } from "../../constants";

type FileToUpload = {
  name: string;
  file: File;
};

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export const useFeatureComments = (
  featureId: string,
  featureKey: string
): {
  comments: FeatureComments;
  submitComment: (...args: any[]) => any;
  isSubmittingComment: boolean;
  submittingCommentId: string;
} => {
  const {
    tokenRetrievalState,
    gqlClientForProject,
    eventLogs
  }: TProjectContext = useContext(ProjectContext);
  const token = tokenRetrievalState?.token;

  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [submittingCommentId, setSubmittingCommentId] = useState("");

  const { data: comments, refetch: refetchComments } =
    useCIQQuery<FeatureComments>(GET_FEATURE_COMMENTS, {
      client: gqlClientForProject,
      variables: {
        where: {
          [featureKey]: { _eq: featureId }
        }
      },
      skip: !gqlClientForProject || !featureId
    });

  const debouncedRefetchCommentsRef: any = useRef<(() => void) | null>(null);

  useEffect(() => {
    debouncedRefetchCommentsRef.current = debounce(refetchComments, 3000);
    return () => {
      debouncedRefetchCommentsRef.current?.cancel();
    };
  }, [refetchComments]);

  const uploadAttachment = useCallback(
    async (attachment: CommentAttachmentResponse, file: File) => {
      await noAuthClient.put(attachment.upload_url, file);
      return attachment;
    },
    []
  );

  const submitComment = useCallback(
    async (commentText: string, filesToUpload: FileToUpload[] = []) => {
      if (!token) {
        message.error(ErrorMessages.comments.addCommentFailed);
        return { success: false };
      }

      setIsSubmittingComment(true);

      try {
        const createResponse: any = await createBulkComments(
          featureId,
          [
            {
              description: commentText.trim(),
              attachments: filesToUpload.map((fileInfo) => ({
                file_name: fileInfo.name
              }))
            }
          ],
          token
        );

        if (createResponse.error) {
          message.error(
            getErrorMessage(
              createResponse.error,
              ErrorMessages.comments.addCommentFailed
            )
          );
          return { success: false, response: createResponse };
        }

        const createdComment = createResponse.data?.comments?.[0];
        const commentId = createdComment?.comment_id;

        if (!commentId) {
          message.error(ErrorMessages.comments.addCommentFailed);
          return { success: false, response: createResponse };
        }

        setSubmittingCommentId(commentId);

        const createdAttachments: CommentAttachmentResponse[] =
          createdComment.attachments || [];

        if (createdAttachments.length) {
          const uploadResults = await Promise.allSettled(
            createdAttachments.map(async (attachment, index) => {
              const fileInfo = filesToUpload[index];
              if (!fileInfo?.file) {
                throw new Error(ErrorMessages.comments.addAttachmentFailed);
              }
              await uploadAttachment(attachment, fileInfo.file);
              return { attachment, fileInfo };
            })
          );

          const failedUploads = uploadResults.filter(
            (result) => result.status === "rejected"
          );

          if (failedUploads.length) {
            const firstFailure = failedUploads[0] as PromiseRejectedResult;
            message.error(
              getErrorMessage(
                firstFailure.reason,
                ErrorMessages.comments.addAttachmentFailed
              )
            );
          }
        }

        refetchComments();
        return { success: true, data: createResponse.data, commentId };
      } catch (error: any) {
        message.error(
          getErrorMessage(error, ErrorMessages.comments.addCommentFailed)
        );
        return { success: false };
      } finally {
        setIsSubmittingComment(false);
        setSubmittingCommentId("");
      }
    },
    [featureId, refetchComments, token, uploadAttachment]
  );

  // Poll the changes and update the respective UI components
  const previousEventLogs = useRef(eventLogs);

  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const isCommentOrAttachmentEvent = eventLogs.some((log) => {
        const info: any = log?.info;
        // The S3-upload ack lambda flips attachments.uploaded and reports it as
        // a feature_instance change, so that event is what reveals a new file.
        const isCommnetOrAttachmentChanged =
          log?.data_source === "comment" ||
          log?.data_source === "attachments" ||
          log?.data_source === "feature_instance" ||
          info?.table_name === "comment" ||
          info?.table_name === "attachments";
        const isRelatedToCurrentFeature: boolean = hasTheFeature(
          featureId,
          log
        );

        return isCommnetOrAttachmentChanged && isRelatedToCurrentFeature;
      });

      if (isCommentOrAttachmentEvent) {
        debouncedRefetchCommentsRef.current?.();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, featureId]);

  return {
    comments: comments as FeatureComments,
    submitComment,
    isSubmittingComment,
    submittingCommentId
  };
};
